import { ChartDataset } from 'chart.js';
import { resolveColorVar } from 'shared-ui';
import { EnforcementStats } from 'types/CarTracking';
import { convertCents, currencyWithoutCents } from 'utils/currency';
import { ComparePeriod, comparePeriodOptions, getAvgSinceTimestamp } from '../../utils';

export type ScansGranularity = 'day' | 'week';
export type ScansLookbackPeriod = ComparePeriod;

export const scansLookbackOptions = comparePeriodOptions;

type DailyScansPoint = {
    date: string;
    rentals: number;
    spaceforceScans: number;
};

export type EnforcementScansChartData = {
    labels: string[];
    tooltipTitles: string[];
    datasets: Array<ChartDataset<'line'>>;
    xAxisTitle: string;
    xAxisMaxTicksLimit: number;
    yAxisMax: number;
    yAxisStep: number;
};

const ENFORCEMENT_SPACEFORCE_SCANS_LINE_COLOR_TOKEN = '--ag-orange-9';
const ENFORCEMENT_SPACEFORCE_SCANS_FILL_COLOR_TOKEN = '--ag-orange-3';
const ENFORCEMENT_RENTALS_LINE_COLOR_TOKEN = '--ag-blue-9';

function getYAxisStep(maxValue: number) {
    if (maxValue > 120) return 20;
    if (maxValue > 60) return 10;
    if (maxValue > 30) return 5;
    if (maxValue > 15) return 2;
    return 1;
}

function parseDateKey(dateKey: string): Date {
    return new Date(`${dateKey}T12:00:00Z`);
}

function getWeekStartDate(date: Date): Date {
    const weekStart = new Date(date);
    weekStart.setUTCHours(0, 0, 0, 0);

    const dayOfWeek = weekStart.getUTCDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    weekStart.setUTCDate(weekStart.getUTCDate() - daysSinceMonday);

    return weekStart;
}

function getRangeStartDate(lookback: ScansLookbackPeriod, currentDate: Date): Date {
    return new Date(getScansSinceTimestamp(lookback, currentDate) * 1000);
}

function formatDateLabel(dateKey: string) {
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(parseDateKey(dateKey));
}

export function getScansSinceTimestamp(lookback: ScansLookbackPeriod, currentDate = new Date()) {
    return getAvgSinceTimestamp(lookback, currentDate);
}

export function getEnforcementStatsGranularity(lookback: ScansLookbackPeriod): ScansGranularity {
    return lookback === '4w' ? 'day' : 'week';
}

export function getLookbackLabel(lookback: ScansLookbackPeriod) {
    return scansLookbackOptions.find((option) => option.value === lookback)?.label ?? 'Last 12 Months';
}

export function getVisibleDailyScans(
    report: EnforcementStats,
    lookback: ScansLookbackPeriod,
    currentDate = new Date()
) {
    const granularity = getEnforcementStatsGranularity(lookback);
    const rangeStartDate =
        granularity === 'week'
            ? getWeekStartDate(getRangeStartDate(lookback, currentDate))
            : getRangeStartDate(lookback, currentDate);

    return Object.entries(report.daily_scans)
        .map(([date, values]) => ({
            date,
            rentals: values.rentals ?? 0,
            spaceforceScans: values.unique_scanned_plates ?? 0,
        }))
        .filter((point) => parseDateKey(point.date) >= rangeStartDate)
        .sort((a, b) => parseDateKey(a.date).getTime() - parseDateKey(b.date).getTime());
}

export function hasRenderableScansData(
    report: EnforcementStats,
    lookback: ScansLookbackPeriod,
    currentDate = new Date()
) {
    return getVisibleDailyScans(report, lookback, currentDate).some(
        (point) => point.rentals > 0 || point.spaceforceScans > 0
    );
}

export function getEnforcementScansChartData(
    report: EnforcementStats,
    lookback: ScansLookbackPeriod,
    currentDate = new Date()
): EnforcementScansChartData {
    const granularity = getEnforcementStatsGranularity(lookback);
    const visibleScans = getVisibleDailyScans(report, lookback, currentDate);
    const labels = visibleScans.map((point) => formatDateLabel(point.date));
    const tooltipTitles = visibleScans.map((point) =>
        granularity === 'week' ? `Week of ${formatDateLabel(point.date)}` : formatDateLabel(point.date)
    );
    const rentalsData = visibleScans.map((point) => point.rentals);
    const spaceforceScansData = visibleScans.map((point) => point.spaceforceScans);
    const maxValue = Math.max(10, ...rentalsData, ...spaceforceScansData);
    const yAxisStep = getYAxisStep(maxValue);
    const yAxisMax = Math.ceil((maxValue + yAxisStep) / yAxisStep) * yAxisStep;

    return {
        labels,
        tooltipTitles,
        xAxisTitle: granularity === 'week' ? 'Week of' : 'Day',
        xAxisMaxTicksLimit: granularity === 'week' ? 8 : 6,
        yAxisStep,
        yAxisMax,
        datasets: [
            {
                label: 'Space Force Scans',
                data: spaceforceScansData,
                borderColor: resolveColorVar(ENFORCEMENT_SPACEFORCE_SCANS_LINE_COLOR_TOKEN),
                backgroundColor: resolveColorVar(ENFORCEMENT_SPACEFORCE_SCANS_FILL_COLOR_TOKEN),
                pointRadius: 0,
                fill: false,
                tension: 0.3,
                borderWidth: 2.5,
            },
            {
                label: 'Rentals',
                data: rentalsData,
                borderColor: resolveColorVar(ENFORCEMENT_RENTALS_LINE_COLOR_TOKEN),
                backgroundColor: resolveColorVar(ENFORCEMENT_RENTALS_LINE_COLOR_TOKEN),
                pointRadius: 0,
                fill: false,
                tension: 0.3,
                borderWidth: 2.5,
            },
        ],
    };
}

export function getAutostartRevenueDisplay(report: EnforcementStats) {
    return currencyWithoutCents.format(convertCents(report.enforcement_autostart_revenue_today));
}

export type { DailyScansPoint };
